import { useEffect, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Layout } from "../components/Layout"
import { Button } from "@/components/Button"
import { ErrorFallback } from "@/components/ErrorBoundary/ErrorBoundary"
import { ConfirmDialog } from "@/components/Dialog/ConfirmDialog"
import { useToast } from "@/components/Toast"
import {
  useCommsOrg,
  usePatchOrg,
  useSetOrgRelevance,
  formatAge,
  FollowupStatus,
} from "@/hooks/useComms"
import { ArrowLeftIcon } from "@heroicons/react/24/outline"

export function CommsOrgDetail() {
  const { publicId } = useParams<{ publicId: string }>()
  const { toast } = useToast()
  const { data, isLoading, error } = useCommsOrg(publicId || "")
  const patchOrg = usePatchOrg()
  const setOrgRelevance = useSetOrgRelevance()

  const [confirmNotRelevant, setConfirmNotRelevant] = useState(false)

  // Seed the note + coverage forms from the org read, keyed off public_id so
  // navigating between two org URLs re-seeds (and a same-org refetch doesn't
  // clobber an in-flight edit).
  const [note, setNote] = useState("")
  const [followupReason, setFollowupReason] = useState("")
  const [seededFor, setSeededFor] = useState<string | null>(null)

  useEffect(() => {
    if (data && seededFor !== data.org.public_id) {
      setNote(data.org.notes ?? "")
      setFollowupReason(data.org.followup_reason ?? "")
      setSeededFor(data.org.public_id)
    }
  }, [data, seededFor])

  if (error) {
    return (
      <Layout>
        <ErrorFallback error={error as Error} />
      </Layout>
    )
  }

  if (isLoading || !data) {
    return (
      <Layout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-1/3 rounded bg-muted" />
          <div className="h-40 rounded bg-muted" />
        </div>
      </Layout>
    )
  }

  const { org, members, rollups } = data
  const isQuarantined = org.is_relevant === false

  const saveCoverage = (status: FollowupStatus) => {
    patchOrg.mutate(
      {
        publicId: org.public_id,
        followup_status: status,
        followup_reason: followupReason.trim() === "" ? null : followupReason,
      },
      {
        onSuccess: () =>
          toast({
            title: status === "active" ? "Coverage cleared" : "Org covered",
            description:
              status === "active"
                ? "Members are back in the follow-up digests (unless they override)."
                : "Members inherit this — they drop out of going-cold / owed-a-reply without per-contact dismissal.",
          }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to update coverage. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const saveNote = () => {
    patchOrg.mutate(
      { publicId: org.public_id, notes: note.trim() === "" ? null : note },
      {
        onSuccess: () => toast({ title: "Note saved" }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to save the note. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  const setRelevance = (relevant: boolean) => {
    setOrgRelevance.mutate(
      { publicId: org.public_id, relevant },
      {
        onSuccess: (res) =>
          toast({
            title: relevant ? "Org restored" : "Org marked not relevant",
            description: relevant
              ? `${res.members_restored ?? 0} member(s) restored.`
              : `${res.members_quarantined ?? 0} member(s) quarantined.`,
          }),
        onError: () =>
          toast({
            title: "Error",
            description: "Failed to update the org. Please try again.",
            variant: "destructive",
          }),
      }
    )
  }

  return (
    <Layout>
      <div className="space-y-6">
        <Link
          to="/comms/orgs"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
        >
          <ArrowLeftIcon className="h-4 w-4" /> All organizations
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-primary">{org.name}</h1>
            <p className="text-muted-foreground">
              {org.primary_domain ? `${org.primary_domain} · ` : ""}
              {rollups.member_count} {rollups.member_count === 1 ? "member" : "members"} · last
              activity {formatAge(rollups.last_activity)}
            </p>
          </div>
          {isQuarantined ? (
            <Button variant="outline" size="sm" onClick={() => setRelevance(true)}>
              Restore org
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-destructive"
              onClick={() => setConfirmNotRelevant(true)}
            >
              Not relevant
            </Button>
          )}
        </div>

        {isQuarantined && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
            This org is marked <span className="font-medium">not relevant</span> — its members were
            quarantined. Restore to bring them back.
          </div>
        )}

        {/* Org coverage (follow-up disposition that members inherit) */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <div className="mb-1 flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-primary">Coverage</h2>
            <span className="text-xs text-muted-foreground">
              {org.followup_status === "active" ? "Not covered" : `Covered · ${org.followup_status.replace("_", " ")}`}
            </span>
          </div>
          <p className="mb-3 text-xs text-muted-foreground">
            Mark the org covered and every member inherits it — they drop out of going-cold /
            owed-a-reply without dismissing each one. A member who sets their own status still wins.
          </p>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Reason (optional)
          </label>
          <input
            type="text"
            value={followupReason}
            onChange={(e) => setFollowupReason(e.target.value)}
            placeholder="e.g. engaged via Quincy + Mike"
            className="mb-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={patchOrg.isPending}
              onClick={() => saveCoverage("no_action")}
            >
              No further follow-up
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={patchOrg.isPending}
              onClick={() => saveCoverage("snoozed")}
            >
              Snooze
            </Button>
            {org.followup_status !== "active" && (
              <Button
                variant="ghost"
                size="sm"
                disabled={patchOrg.isPending}
                onClick={() => saveCoverage("active")}
              >
                Clear coverage
              </Button>
            )}
          </div>
        </section>

        {/* Org note */}
        <section className="rounded-lg border border-border bg-surface p-4">
          <h2 className="mb-2 text-sm font-semibold text-primary">Note</h2>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Context about this organization…"
            className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mt-2 flex justify-end">
            <Button size="sm" disabled={patchOrg.isPending} onClick={saveNote}>
              Save note
            </Button>
          </div>
        </section>

        {/* Members */}
        <section>
          <h2 className="mb-2 text-lg font-semibold text-primary">
            Members <span className="text-sm font-normal text-muted-foreground">({members.length})</span>
          </h2>
          {members.length === 0 ? (
            <p className="text-sm italic text-muted-foreground">
              No members yet — assign contacts from their detail page.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
              {members.map((m) => (
                <li key={m.public_id}>
                  <Link
                    to={`/comms/contacts/${m.public_id}`}
                    className="flex items-center justify-between px-4 py-3 hover:bg-muted"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-primary">
                        {m.display_name || m.primary_email}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{m.primary_email}</p>
                    </div>
                    <div className="ml-4 flex shrink-0 items-center gap-2">
                      {m.is_relevant === false && (
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                          not relevant
                        </span>
                      )}
                      {m.followup_status !== "active" && (
                        <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-800">
                          {m.followup_status.replace("_", " ")}
                        </span>
                      )}
                      <span className="text-xs text-muted-foreground">
                        {formatAge(m.last_contacted_at)}
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <ConfirmDialog
        open={confirmNotRelevant}
        onOpenChange={setConfirmNotRelevant}
        title="Mark org not relevant?"
        description={`All ${rollups.member_count} member(s) of ${org.name} will be hidden from comms views. Their own per-contact status is untouched, and restoring the org brings them all back — nothing is queued for purge.`}
        confirmLabel="Not relevant"
        confirmVariant="destructive"
        onConfirm={() => setRelevance(false)}
      />
    </Layout>
  )
}
